import { useState, useRef, useEffect, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX,
  Loader2,
  ChevronDown,
  BookOpen
} from "lucide-react";
import { SURAHS, getSurah } from "@/data/quranData";
import { Slider } from "@/components/ui/slider";

interface Ayah {
  number: number;
  numberInSurah: number;
  text: string;
  audio: string;
}

interface Translation {
  text: string;
}

interface QuranPlayerProps {
  initialSurah?: number;
  initialAyah?: number;
  onAyahChange?: (surah: number, ayah: number) => void;
}

const RECITERS = [
  { id: "ar.alafasy", name: "Mishary Al-Afasy" },
  { id: "ar.abdulbasitmurattal", name: "Abdul Basit (Murattal)" },
  { id: "ar.minshawi", name: "Mohamed Al-Minshawi" },
  { id: "ar.husary", name: "Mahmoud Al-Hussary" },
];

export const QuranPlayer = forwardRef<HTMLDivElement, QuranPlayerProps>(({ 
  initialSurah = 1, 
  initialAyah = 1,
  onAyahChange 
}, ref) => {
  const [currentSurah, setCurrentSurah] = useState(initialSurah);
  const [currentAyahIndex, setCurrentAyahIndex] = useState(initialAyah - 1);
  const [ayahs, setAyahs] = useState<Ayah[]>([]);
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [reciter, setReciter] = useState(RECITERS[0].id);
  const [showReciterPicker, setShowReciterPicker] = useState(false);
  const [showSurahPicker, setShowSurahPicker] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  
  const surahInfo = getSurah(currentSurah);
  const currentAyah = ayahs[currentAyahIndex];
  const currentTranslation = translations[currentAyahIndex];

  // Fetch surah data when surah or reciter changes
  useEffect(() => {
    const fetchSurah = async () => {
      setIsLoading(true);
      setError(null);
      setIsPlaying(false);
      
      try {
        // Fetch Arabic text with audio
        const arabicRes = await fetch(
          `https://api.alquran.cloud/v1/surah/${currentSurah}/${reciter}`
        );
        const arabicData = await arabicRes.json();
        
        // Fetch English translation
        const translationRes = await fetch(
          `https://api.alquran.cloud/v1/surah/${currentSurah}/en.sahih`
        );
        const translationData = await translationRes.json();
        
        if (arabicData.status === "OK" && translationData.status === "OK") {
          setAyahs(arabicData.data.ayahs);
          setTranslations(translationData.data.ayahs);
          setCurrentAyahIndex(0);
        } else {
          setError("Failed to load surah");
        }
      } catch (err) {
        setError("Network error. Please try again.");
        console.error("Quran API error:", err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSurah();
  }, [currentSurah, reciter]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  // Notify parent of ayah changes
  useEffect(() => {
    if (onAyahChange && currentAyah) {
      onAyahChange(currentSurah, currentAyah.numberInSurah);
    }
  }, [currentAyah, currentSurah, onAyahChange]);

  const handlePlay = () => {
    if (!audioRef.current || !currentAyah) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePrevious = () => {
    if (currentAyahIndex > 0) {
      setCurrentAyahIndex(currentAyahIndex - 1);
      setIsPlaying(false);
    } else if (currentSurah > 1) {
      // Go to previous surah
      setCurrentSurah(currentSurah - 1);
    }
  };

  const handleNext = () => {
    if (currentAyahIndex < ayahs.length - 1) {
      setCurrentAyahIndex(currentAyahIndex + 1);
      setIsPlaying(false);
    } else if (currentSurah < 114) {
      // Go to next surah
      setCurrentSurah(currentSurah + 1);
    }
  };

  const handleAudioEnded = () => {
    // Auto-advance to next ayah
    if (currentAyahIndex < ayahs.length - 1) {
      setCurrentAyahIndex(currentAyahIndex + 1);
      // Will auto-play due to useEffect
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
        }
      }, 300);
    } else {
      setIsPlaying(false);
    }
  };

  const selectSurah = (surahNumber: number) => {
    setCurrentSurah(surahNumber);
    setCurrentAyahIndex(0);
    setShowSurahPicker(false);
    setIsPlaying(false);
  };

  return (
    <div ref={ref} className="space-y-4" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-muted-foreground">
          <BookOpen className="w-4 h-4" />
          <span className="text-xs uppercase tracking-wider">Quran Player</span>
        </div>
        
        {/* Reciter Selector */}
        <div className="relative">
          <button
            onClick={() => setShowReciterPicker(!showReciterPicker)}
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            {RECITERS.find(r => r.id === reciter)?.name}
            <ChevronDown className={`w-3 h-3 transition-transform ${showReciterPicker ? 'rotate-180' : ''}`} />
          </button>
          
          <AnimatePresence>
            {showReciterPicker && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="absolute right-0 top-6 z-10 bg-background border border-border rounded-sm shadow-lg min-w-[180px]"
              >
                {RECITERS.map(r => (
                  <button
                    key={r.id}
                    onClick={() => {
                      setReciter(r.id);
                      setShowReciterPicker(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                      reciter === r.id ? 'bg-primary/10 text-primary' : ''
                    }`}
                  >
                    {r.name}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Surah Selector */}
      <div className="relative">
        <button
          onClick={() => setShowSurahPicker(!showSurahPicker)}
          className="w-full flex items-center justify-between p-3 bg-cream border border-border rounded-sm hover:bg-accent transition-colors"
        >
          <div className="text-left">
            <p className="text-sm font-medium">{surahInfo?.name}</p>
            <p className="text-xs text-muted-foreground">{surahInfo?.englishName}</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showSurahPicker ? 'rotate-180' : ''}`} />
        </button>
        
        <AnimatePresence>
          {showSurahPicker && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="max-h-48 overflow-y-auto border border-t-0 border-border rounded-b-sm bg-background">
                {SURAHS.map(surah => (
                  <button
                    key={surah.number}
                    onClick={() => selectSurah(surah.number)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors flex items-center justify-between ${
                      currentSurah === surah.number ? 'bg-primary/10' : ''
                    }`}
                  >
                    <span>
                      <span className="text-muted-foreground mr-2">{surah.number}.</span>
                      {surah.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {surah.ayahCount} ayahs
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Ayah Display */}
      <div className="bg-background border border-border rounded-sm p-4 min-h-[120px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-24 text-destructive text-sm">
            {error}
          </div>
        ) : currentAyah ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Ayah {currentAyah.numberInSurah} of {ayahs.length}</span>
              <span>Surah {currentSurah}</span>
            </div>
            
            {/* Arabic Text */}
            <p 
              className="text-xl md:text-2xl text-right font-arabic leading-loose text-foreground"
              dir="rtl"
            >
              {currentAyah.text}
            </p>
            
            {/* Translation */}
            {currentTranslation && (
              <p className="text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">
                {currentTranslation.text}
              </p>
            )}
          </div>
        ) : null}
      </div>

      {/* Ayah Progress */}
      {ayahs.length > 0 && (
        <div className="space-y-1">
          <Slider
            value={[currentAyahIndex]}
            max={ayahs.length - 1}
            step={1}
            onValueChange={(value) => {
              setCurrentAyahIndex(value[0]);
              setIsPlaying(false);
            }}
            className="cursor-pointer"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Ayah {currentAyahIndex + 1}</span>
            <span>{ayahs.length} total</span>
          </div>
        </div>
      )}

      {/* Audio Controls */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={handlePrevious}
          disabled={currentAyahIndex === 0 && currentSurah === 1}
          className="w-10 h-10 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SkipBack className="w-5 h-5" />
        </button>
        
        <button
          onClick={handlePlay}
          disabled={isLoading || !currentAyah}
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPlaying ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Play className="w-6 h-6 ml-1" />
          )}
        </button>
        
        <button
          onClick={handleNext}
          disabled={currentAyahIndex === ayahs.length - 1 && currentSurah === 114}
          className="w-10 h-10 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SkipForward className="w-5 h-5" />
        </button>
      </div>

      {/* Volume Control */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsMuted(!isMuted)}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <Slider
          value={[isMuted ? 0 : volume]}
          max={100}
          step={1}
          onValueChange={(value) => {
            setVolume(value[0]);
            setIsMuted(value[0] === 0);
          }}
          className="flex-1"
        />
      </div>

      {/* Hidden Audio Element */}
      {currentAyah && (
        <audio
          ref={audioRef}
          src={currentAyah.audio}
          onEnded={handleAudioEnded}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      )}
    </div>
  );
});

QuranPlayer.displayName = "QuranPlayer";
